// React and related imports
import React, { useEffect } from 'react';

// i18 Translation
import { TFunction } from 'i18next';

// Redux hooks and actions
import { useAppDispatch } from '../../../redux/hooks/useAppDispatch';
import {
  setValidationErrors,
  clearValidationErrors,
} from '../../../redux/slices/sessionSlice';

// Types and Interfaces
import { IMultiLanguageValue } from '../../../common/interfaces/MultiLanguageValue.interface';
import { SessionStatus } from '../../../common/enums/SessionStatus';

// Custom Components
import { MultiLanguageFieldGroup } from '../../Shared/MultiLanguageInput';

interface SessionState {
  title: IMultiLanguageValue[];
  description: IMultiLanguageValue[];
  url: string;
  status: SessionStatus;
}

interface MultiLangSessionInputsProps {
  t: TFunction<'translation', undefined>;
  sessionState: SessionState;
  updateField: (
    field: keyof SessionState,
    value: IMultiLanguageValue[]
  ) => void;
}

export const MultiLangSessionInputs: React.FC<MultiLangSessionInputsProps> = ({
  t,
  sessionState,
  updateField,
}) => {
  const dispatch = useAppDispatch();

  // Validation effects
  useEffect(() => {
    const titles = sessionState?.title;
    const hasValidTitle = titles?.some(
      (titleItem: IMultiLanguageValue) => titleItem.value.trim() !== ''
    );
    const longTitle = titles?.find(
      (titleItem: IMultiLanguageValue) => titleItem.value.trim().length > 100
    )?.value;

    if (!hasValidTitle) {
      dispatch(
        setValidationErrors({
          field: 'title',
          errors: ['sessionTitleRequired'],
        })
      );
    } else if (longTitle) {
      dispatch(
        setValidationErrors({
          field: 'title',
          errors: ['sessionTitleMaxLength'],
        })
      );
    } else {
      dispatch(clearValidationErrors('title'));
    }
  }, [sessionState?.title, dispatch]);

  useEffect(() => {
    const descriptions = sessionState?.description;
    const hasValidDesc = descriptions?.some(
      (descItem: IMultiLanguageValue) => descItem.value.trim() !== ''
    );
    const longDesc = descriptions?.find(
      (descItem: IMultiLanguageValue) => descItem.value.trim().length > 500
    )?.value;

    if (!hasValidDesc) {
      dispatch(
        setValidationErrors({
          field: 'description',
          errors: ['sessionDescriptionRequired'],
        })
      );
    } else if (longDesc) {
      dispatch(
        setValidationErrors({
          field: 'description',
          errors: ['sessionDescriptionMaxLength'],
        })
      );
    } else {
      dispatch(clearValidationErrors('description'));
    }
  }, [sessionState?.description, dispatch]);

  return (
    <MultiLanguageFieldGroup
      fields={[
        {
          id: 'title-input',
          label: 'sessions.title',
          type: 'text',
          placeholder: 'placeholders.title',
          required: true,
          autoFocus: true,
          validation: {
            maxLength: 100,
          },
        },
        {
          id: 'description-input',
          label: 'sessions.description',
          type: 'textarea',
          placeholder: 'placeholders.description',
          required: true,
          rows: 4,
          className: 'mt-1',
          validation: {
            maxLength: 500,
          },
        },
      ]}
      values={{
        'title-input': sessionState?.title || [],
        'description-input': sessionState?.description || [],
      }}
      onChange={(fieldId, value) => {
        if (fieldId === 'title-input') {
          updateField('title' as keyof SessionState, value);
        } else if (fieldId === 'description-input') {
          updateField('description' as keyof SessionState, value);
        }
      }}
      sharedLanguageSelector={true}
      cardWrapper={true}
      t={t}
    />
  );
};
